// URDPurchase.jsx - Complete with Enhanced UI matching Customers Page
import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { DataContext } from '../../context/DataContext';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Grid,
  alpha,
  Drawer,
  Alert,
  useTheme,
  Chip,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  Person,
  Event,
  Receipt,
  Inventory,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";

ModuleRegistry.registerModules([AllCommunityModule]);

// ============== HELPER FUNCTIONS ==============

// Format date - Updated to handle ISO dates
const formatDate = (dateString) => {
  if (!dateString) return '';

  // If it's an ISO date string (contains 'T')
  if (dateString.includes('T')) {
    try {
      const date = new Date(dateString);
      // Format as DD/MM/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  }

  // Remove leading quote if present (for your existing format)
  const cleanDate = dateString.replace(/^'/, '');
  return cleanDate;
};

// Indian Number to Words Conversion
const numberToWords = (num) => {
  if (typeof num !== 'number') num = parseFloat(num) || 0;
  num = Math.floor(num);

  if (num === 0) return 'Zero Rupees Only';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanOneThousand = (n) => {
    if (n === 0) return '';

    let result = '';

    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }

    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }

    if (n > 0) {
      result += ones[n] + ' ';
    }

    return result.trim();
  };

  const convertToIndianWords = (n) => {
    if (n === 0) return '';

    let crore = Math.floor(n / 10000000);
    n %= 10000000;

    let lakh = Math.floor(n / 100000);
    n %= 100000;

    let thousand = Math.floor(n / 1000);
    n %= 1000;

    let hundred = n;

    let result = '';

    if (crore > 0) {
      result += convertLessThanOneThousand(crore) + ' Crore ';
    }

    if (lakh > 0) {
      result += convertLessThanOneThousand(lakh) + ' Lakh ';
    }

    if (thousand > 0) {
      result += convertLessThanOneThousand(thousand) + ' Thousand ';
    }

    if (hundred > 0) {
      result += convertLessThanOneThousand(hundred);
    }

    return result.trim();
  };

  const words = convertToIndianWords(num);
  return words ? words + ' Rupees Only' : 'Zero Rupees Only';
};

// Get CSS styles for printing
const getBillStyles = () => `
/* Base styles for bill printing */
@media print {
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
  }
  
  body * {
    visibility: hidden !important;
  }
  
  #billContent {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 10px 10px !important;
    box-shadow: none !important;
    background: white !important;
    visibility: visible !important;
  }
  
  #billContent * {
    visibility: visible !important;
  }
  
  table {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  tr {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  table, th, td {
    border: 1px solid black !important;
    border-collapse: collapse !important;
  }
  
  @page {
    size: A4 portrait;
    margin: 5mm 5mm;
    margin-top: 10mm;
  }
  
  [style*="background-color"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  p, td, th {
    orphans: 3;
    widows: 3;
  }
  
  table {
    font-size: 11px !important;
  }
  
  #billContent {
    padding: 5mm 8mm !important;
  }
}

/* Screen styles */
body {
  font-family: 'Times New Roman', serif;
  background: #f5f5f5;
  margin: 0;
  padding: 20px;
}

#billContent {
  max-width: 750px;
  width: 100%;
  margin: 0 auto;
  background: white;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  box-sizing: border-box;
  font-size: 13px;
}

[style*="background-color: #f9f1b0"] {
  background-color: #f9f1b0 !important;
  border-bottom: 2px solid #d4b44a;
  padding: 8px 0 !important;
  margin-bottom: 10px;
}

[style*="color: #b01c1c"] {
  color: #b01c1c !important;
  font-weight: bold;
  font-size: 16px !important;
  margin: 5px 0 8px 0 !important;
  text-align: center;
}

[style*="display: flex; align-items: center; justify-content: space-around"] {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0 20px !important;
  margin-bottom: 5px;
}

[style*="height: 70px"] img {
  height: 60px !important;
  width: auto !important;
  border-radius: 30px !important;
  display: block;
}

[style*="SUPRIYA JEWELLERS"] h1 {
  font-size: 24px !important;
  font-weight: 900 !important;
  color: #c00 !important;
  margin: 0 !important;
  line-height: 1.2;
  letter-spacing: 0.5px;
}

[style*="Specialist in Gold"] h3 {
  font-size: 16px !important;
  font-weight: 600 !important;
  margin: 3px 0 0 0 !important;
  color: #333;
}

[style*="flex: 1; display: flex; justify-content: flex-end"] {
  text-align: right;
  font-size: 12px !important;
  color: #333;
  margin-top: 5px;
}

[style*="font-size: 13px; color: #333; padding-top: 5px"] {
  font-size: 11px !important;
  color: #555 !important;
  padding-top: 3px !important;
  text-align: center;
  line-height: 1.3;
}

table {
  width: 100% !important;
  border-collapse: collapse !important;
  margin: 8px 0 !important;
  font-size: 12px !important;
}

table th {
  background-color: #f3f3f3 !important;
  font-weight: bold;
  padding: 6px 4px !important;
  text-align: center;
  border: 1px solid #000 !important;
  vertical-align: middle;
}

table td {
  padding: 4px !important;
  text-align: center;
  border: 1px solid #000 !important;
  vertical-align: middle;
}

[style*="background-color: #f9f1b0; border: 1px solid #d4b44a"] {
  background-color: #f9f1b0 !important;
  border: 1px solid #d4b44a !important;
  border-radius: 5px !important;
  padding: 10px !important;
}

br {
  margin: 0 !important;
  padding: 0 !important;
}

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}
`;

// ============== CUSTOM RENDERERS ==============

const ActionsRenderer = (props) => {
  const handlePrint = () => {
    props.context.handlePrint(props.data);
  };

  return (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
      <Tooltip title="Print Bill">
        <IconButton size="small" onClick={handlePrint}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ============== MAIN COMPONENT ==============

const URDPurchase = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { urdPurchase, addUrdPurchase, loading: contextLoading } = useContext(DataContext);

  // AG Grid ref
  const gridRef = useRef();
  const [isGridReady, setIsGridReady] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filterStatus = 'all';
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [isSaving, setIsSaving] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    address: '',
    aadharCardNo: '',
    panNo: '',
    date: new Date().toLocaleDateString('en-CA'),
    receiverName: '',
    billNo: '',
    itemDetails: [
      {
        id: 1,
        itemName: '',
        metal: '',
        melting: '',
        weight: '',
        rate: '',
        amount: 0
      }
    ],
    totalAmount: 0,
    amountInWords: '',
    declaration: 'I hereby declare that the above-mentioned goods are my personal property and I am selling them voluntarily. This transaction is not forced by anyone and is done with my free will and consent.'
  });

  const [printData, setPrintData] = useState(null);

  // Generate next bill number
  const generateBillNumber = useCallback(() => {
    if (!urdPurchase || urdPurchase.length === 0) {
      return 'URD-1';
    }

    const sorted = [...urdPurchase].sort((a, b) => {
      const numA = parseInt(a.billNo?.replace('URD-', '') || 0);
      const numB = parseInt(b.billNo?.replace('URD-', '') || 0);
      return numB - numA;
    });

    const latest = sorted[0];
    if (latest && latest.billNo) {
      const num = parseInt(latest.billNo.replace('URD-', ''));
      return `URD-${num + 1}`;
    }
    return 'URD-1';
  }, [urdPurchase]);

  // Set initial bill number
  useEffect(() => {
    if (!formData.billNo) {
      setFormData(prev => ({
        ...prev,
        billNo: generateBillNumber()
      }));
    }
  }, [urdPurchase, formData.billNo, generateBillNumber]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle item changes
  const handleItemChange = (id, field, value) => {
    setFormData(prev => {
      const updatedItems = prev.itemDetails.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };

          if (field === 'weight' || field === 'melting' || field === 'rate') {
            const weight = parseFloat(field === 'weight' ? value : item.weight) || 0;
            const melting = parseFloat(field === 'melting' ? value : item.melting) || 0;
            const rate = parseFloat(field === 'rate' ? value : item.rate) || 0;

            if (field === 'melting' && (parseFloat(value) < 0 || parseFloat(value) > 100)) {
              return item;
            }

            updatedItem.amount = (weight * melting * rate) / 100;
          }
          return updatedItem;
        }
        return item;
      });

      const total = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);

      return {
        ...prev,
        itemDetails: updatedItems,
        totalAmount: total,
        amountInWords: numberToWords(total)
      };
    });
  };

  // Add new item row
  const addItemRow = () => {
    const newId = Math.max(...formData.itemDetails.map(i => i.id), 0) + 1;
    setFormData(prev => ({
      ...prev,
      itemDetails: [
        ...prev.itemDetails,
        {
          id: newId,
          itemName: '',
          metal: '',
          melting: '',
          weight: '',
          rate: '',
          amount: 0
        }
      ]
    }));
  };

  // Remove item row
  const removeItemRow = (id) => {
    if (formData.itemDetails.length <= 1) return;
    setFormData(prev => {
      const updatedItems = prev.itemDetails.filter(item => item.id !== id);
      const total = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      return {
        ...prev,
        itemDetails: updatedItems,
        totalAmount: total,
        amountInWords: numberToWords(total)
      };
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      phoneNumber: '',
      address: '',
      aadharCardNo: '',
      panNo: '',
      date: new Date().toISOString().split('T')[0],
      receiverName: '',
      billNo: generateBillNumber(),
      itemDetails: [
        {
          id: 1,
          itemName: '',
          metal: '',
          melting: '',
          weight: '',
          rate: '',
          amount: 0
        }
      ],
      totalAmount: 0,
      amountInWords: '',
      declaration: 'I hereby declare that the above-mentioned goods are my personal property and I am selling them voluntarily. This transaction is not forced by anyone and is done with my free will and consent.'
    });
  };

  // Show alert
  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  }, []);

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      showAlert('Please enter dealer name', 'error');
      return;
    }
    if (!formData.receiverName) {
      showAlert('Please enter receiver name', 'error');
      return;
    }

    const validItems = formData.itemDetails.filter(item => item.itemName.trim() !== '');
    if (validItems.length === 0) {
      showAlert('Please add at least one item', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const result = await addUrdPurchase(formData);
      if (result?.success) {
        showAlert('URD Purchase saved successfully!', 'success');
        setIsDrawerOpen(false);
        resetForm();
      } else {
        showAlert(result?.error || 'Failed to save URD Purchase', 'error');
      }
    } catch (error) {
      console.error('Error saving URD Purchase:', error);
      showAlert('An error occurred while saving', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Generate URD Purchase Bill HTML
  const generateURDPurchaseBillHTML = (data) => {
    let itemsHTML = '';
    let serialNo = 1;

    data.itemDetails.forEach((item) => {
      if (item.itemName) {
        itemsHTML += `
          <tr>
            <td style="border: 1px solid #000; padding: 6px; text-align: center;">${serialNo}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: left;">${item.itemName}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: center;">${item.metal || '-'}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: center;">${item.melting || '-'}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: center;">${item.weight || '0.00'}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: center;">${item.rate || '0.00'}</td>
            <td style="border: 1px solid #000; padding: 6px; text-align: right;">${(item.amount || 0).toFixed(2)}</td>
          </tr>
        `;
        serialNo++;
      }
    });

    const currentRowCount = data.itemDetails.filter(item => item.itemName).length;
    const minTotalRows = 10;
    const rowsNeeded = minTotalRows - (currentRowCount + 1);
    const fillerHeight = Math.max(0, rowsNeeded * 25);

    if (fillerHeight > 0) {
      itemsHTML += `
        <tr style="height: ${fillerHeight}px;">
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
          <td style="border: 1px solid #000; padding: 4px;">&nbsp;</td>
        </tr>`;
    }

    itemsHTML += `
      <tr>
        <td colspan="6" style="border: 1px solid #000; padding: 6px; text-align: right; font-weight: bold;">Total Amount</td>
        <td style="border: 1px solid #000; padding: 6px; text-align: right; font-weight: bold;">${data.totalAmount.toFixed(2)}</td>
      </tr>`;

    return `
      <div id="billContent" style="font-family: 'Times New Roman', serif; padding: 15px; width: 750px; margin: 0 auto; background: white; border: 1px solid black">
        <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); opacity: 0.1; z-index: 9999; pointer-events: none;">
          <img src="/assets/images/logo.jpeg" alt="Logo" style="width: 400px; height: 400px; object-fit: contain">
        </div>
        
        <div style="width: 100%; background-color: #f9f1b0; text-align: center; border-bottom: 3px solid #d4b44a; padding: 10px 0;">
          <div style="color: #b01c1c; font-weight: bold; font-size: 16px; margin-bottom: 10px;">|| श्री गणेशाय नमः ||</div>
          <div style="display: flex; align-items: center; justify-content: space-around; padding: 0 40px;">
            <div style="flex: 1;">
              <img src="/assets/images/logo.jpeg" alt="Supriya Jewellers Logo" style="height: 70px; width: auto; border-radius: 35px;">
            </div>
            <div style="flex: 3; text-align: center;">
              <h1 style="margin: 0; font-size: 30px; font-weight: 900; color: #c00;">SUPRIYA JEWELLERS</h1>
              <h3 style="margin: 5px 0 0; font-weight: 600;">Specialist in Gold & Silver Ornaments</h3>
            </div>
            <div style="flex: 1; display: flex; justify-content: flex-end;">
              <p><strong>Mob.:</strong>93727 98142</p>
            </div>
          </div>
          <div style="font-size: 13px; color: #333; padding-top: 5px;">
            Shop No. 6 & 7, Shrinath Building, B.P Road, Near Goddev Naka, Bhayandar (East), Dist. Thane - 401105
          </div>
        </div>
        <hr style="border: 1px solid #000; margin: 10px 0;">
        
        <h3 style="text-align:center; margin:5px 0; color: #b01c1c;">URD PURCHASE RECEIPT</h3>
        <p style="text-align:center; margin:2px 0 10px 0; font-size:12px; color:#666;">(Unregistered Dealer Purchase)</p>

        <div style="display:flex; justify-content:space-between; font-size:13px; margin-bottom: 15px;">
          <div style="flex: 1;">
            <p><strong>Bill No:</strong> <span>${data.billNo || 'N/A'}</span></p>
            <p><strong>Dealer Name:</strong> <span>${data.name || ''}</span></p>
            <p><strong>Address:</strong> <span>${data.address || ''}</span></p>
            <p><strong>Aadhar Card:</strong> <span>${data.aadharCardNo || ''}</span></p>
          </div>
          <div style="flex: 1; text-align: right;">
            <p><strong>Date:</strong> <span>${formatDate(data.date)}</span></p>
            <p><strong>Receiver Name:</strong> <span>${data.receiverName || ''}</span></p>
            <p><strong>Phone:</strong> <span>${data.phoneNumber || ''}</span></p>
            <p><strong>PAN Card:</strong> <span>${data.panNo || ''}</span></p>
          </div>
        </div>
        
        <table border="1" cellspacing="0" cellpadding="4" width="100%" style="font-size:13px; border-collapse: collapse; text-align:center; margin-top:5px;">
          <thead>
            <tr>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 50px;">Sr. No.</th>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 150px;">Item Name</th>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 80px;">Metal</th>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 80px;">Melting %</th>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 80px;">Weight (gm)</th>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 80px;">Rate (₹/gm)</th>
              <th style="border: 1px solid #000; padding: 6px; background-color: #f3f3f3; width: 100px;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>

        <div style="margin: 15px 0; border-top: 2px solid #999; padding-top: 10px; font-size: 13px;">
          <strong>Total Amount (in words):</strong><br>
          <span>INR ${numberToWords(data.totalAmount).toUpperCase()}</span>
        </div>

        <div style="margin: 15px 0; padding: 10px; background-color: #f9f1b0; border: 1px solid #d4b44a; border-radius: 5px; font-size: 12px;">
          <p style="margin: 0; font-style: italic; text-align: center;">
            <strong>Declaration:</strong> ${data.declaration || 'I hereby declare that the above-mentioned goods are my personal property and I am selling them voluntarily. This transaction is not forced by anyone and is done with my free will and consent.'}
          </p>
        </div>

        <div style="display:flex; justify-content:space-between; font-size:13px; margin-top:20px; border-top: 1px solid #999; padding-top: 15px;">
          <div>
            <p><strong>Seller's Signature</strong></p>
            <div style="margin-top: 5px;">________________________</div>
            <p style="font-size: 11px; color: #666; margin-top: 5px;">(${data.name || 'Unregistered Dealer'})</p>
          </div>
          <div style="text-align: right;">
            <p><strong>Receiver's Signature</strong></p>
            <div style="margin-top: 5px;">________________________</div>
            <p style="font-size: 11px; color: #666; margin-top: 5px;">(Supriya Jewellers)</p>
          </div>
        </div>

        <div style="margin-top: 15px; font-size: 11px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 10px;">
          <p style="margin: 0;">This is a system-generated receipt for the purchase of gold/silver ornaments from an unregistered dealer.</p>
          <p style="margin: 2px 0 0 0;">All transactions are subject to the terms and conditions of Supriya Jewellers.</p>
        </div>
      </div>
    `;
  };

  // Handle Print
  const handlePrint = useCallback((purchaseData) => {
    const data = purchaseData || formData;
    const validItems = data.itemDetails.filter(item => item.itemName.trim() !== '');

    if (validItems.length === 0) {
      showAlert('No items to print', 'error');
      return;
    }

    const html = generateURDPurchaseBillHTML(data);
    setPrintData(html);

    setTimeout(() => {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>URD Purchase Receipt</title>
              <style>
                ${getBillStyles()}
                body { background: white; margin: 0; padding: 20px; }
                #billContent { max-width: 750px; margin: 0 auto; }
              </style>
            </head>
            <body>
              ${html}
              <script>
                window.onload = function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 1000);
                };
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }, 300);
  }, [setPrintData, formData, showAlert]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsPageLoading(true);
    try {
      // Refresh is handled by the context
      showAlert('Data refreshed successfully', 'success');
    } catch (error) {
      showAlert('Failed to refresh data', 'error');
    } finally {
      setTimeout(() => setIsPageLoading(false), 500);
    }
  }, [showAlert]);

  // Column Definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr. No.",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        sortable: false,
        filter: false,
        pinned: "left",
        cellStyle: { textAlign: "center", fontWeight: 600 },
      },
      {
        field: "billNo",
        headerName: "Bill No",
        width: 120,
        filter: "agTextColumnFilter",
        sortable: true,
        cellStyle: { fontWeight: 600 },
      },
      {
        field: "name",
        headerName: "Dealer Name",
        width: 180,
        filter: "agTextColumnFilter",
        sortable: true,
      },
      {
        field: "phoneNumber",
        headerName: "Phone",
        width: 130,
        filter: "agTextColumnFilter",
        sortable: true,
        valueFormatter: (params) => {
          if (!params.value) return '';
          // Convert to string and format if needed
          const phoneStr = String(params.value);
          // Remove any non-digit characters for display
          return phoneStr;
        },
        cellStyle: {
          fontFamily: 'monospace',
          fontSize: '0.9rem'
        },
      },
      {
        field: "date",
        headerName: "Date",
        width: 120,
        filter: "agDateColumnFilter",
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        },
        sortable: true,
      },
      {
        field: "itemDetails",
        headerName: "Items",
        width: 80,
        filter: false,
        sortable: false,
        valueGetter: (params) => params.data?.itemDetails?.length || 0,
        cellStyle: { textAlign: "center" },
      },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        width: 140,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueFormatter: (params) => `₹${(params.value || 0).toFixed(2)}`,
        cellStyle: { textAlign: "right", fontWeight: 600 },
      },
      {
        headerName: "Actions",
        width: 120,
        filter: false,
        sortable: false,
        cellRenderer: ActionsRenderer,
        cellRendererParams: {
          context: {
            handlePrint,
          },
        },
        pinned: "right",
      },
    ],
    [handlePrint]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 80,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        borderRight: isDark
          ? "1px solid rgba(255, 255, 255, 0.05)"
          : "1px solid rgba(224, 224, 224, 0.5)",
        display: "flex",
        alignItems: "center",
        whiteSpace: 'normal',
        wordWrap: 'break-word',
      },
    }),
    [isDark]
  );

  // Grid options
  const gridOptions = useMemo(
    () => ({
      rowBuffer: 20,
      cacheBlockSize: 100,
      maxBlocksInCache: 10,
      animateRows: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      suppressColumnVirtualisation: false,
      suppressRowVirtualisation: false,
    }),
    []
  );

  // Handle search filtering
  useEffect(() => {
    if (!isGridReady || !gridRef.current?.api) return;

    const timer = setTimeout(() => {
      try {
        if (searchTerm.trim()) {
          const filterModel = {};
          const searchableColumns = ["name", "billNo", "phoneNumber", "receiverName"];

          searchableColumns.forEach((column) => {
            filterModel[column] = {
              type: "contains",
              filter: searchTerm,
              filterParams: {
                caseSensitive: false,
              },
            };
          });

          gridRef.current.api.setFilterModel(filterModel);
        } else {
          gridRef.current.api.setFilterModel(null);
          if (filterStatus !== 'all') {
            gridRef.current.api.setFilterModel({
              status: {
                type: "equals",
                filter: filterStatus,
              },
            });
          }
        }
      } catch (error) {
        console.error("Error applying search filter:", error);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isGridReady, filterStatus]);

  // Custom loading overlay
  const loadingOverlayComponent = useMemo(() => {
    return function CustomLoadingOverlay() {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#2563EB" }} />
        </Box>
      );
    };
  }, []);

  // Custom no rows overlay
  const noRowsOverlayComponent = useMemo(() => {
    return function CustomNoRowsOverlay() {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h6" sx={{ color: isDark ? "#a0a0a0" : "#666" }}>
            {searchTerm || filterStatus !== 'all'
              ? "No URD purchases found matching your filters"
              : "No URD purchases found"}
          </Typography>
        </Box>
      );
    };
  }, [isDark, searchTerm, filterStatus]);

  const isLoading = contextLoading || isPageLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} thickness={4} sx={{ color: "#2563EB", mb: 2 }} />
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 600, color: isDark ? "#e0e0e0" : "#1a1a1a" }}>
            Loading URD purchases...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 2,
        color: isDark ? "#e0e0e0" : "#1a1a1a",
      }}
    >
      <Container maxWidth="xl">
        {/* Alert */}
        {alertMessage && (
          <Alert
            severity={alertType}
            sx={{ mb: 3 }}
            onClose={() => setAlertMessage("")}
          >
            {alertMessage}
          </Alert>
        )}

        {/* Header Section */}
        <Card
          sx={{
            mb: 3,
            background: isDark
              ? "rgba(255, 255, 255, 0.1)"
              : "rgba(255, 255, 255, 0.2)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    mb: 0.5,
                    color: isDark ? "#e0e0e0" : "#1a1a1a",
                  }}
                >
                  URD Purchase
                </Typography>
                <Typography
                  sx={{
                    color: isDark ? "#a0a0a0" : "#666",
                    fontSize: "1rem",
                  }}
                >
                  Total Purchases:{" "}
                  <Chip
                    label={urdPurchase?.length || 0}
                    color="primary"
                    size="small"
                  />
                </Typography>
              </Box>

              <TextField
                placeholder="Search by Dealer Name, Bill No, or Receiver..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color: isDark ? "#a0a0a0" : "#666",
                          fontSize: 20,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 250,
                  "& .MuiOutlinedInput-root": {
                    color: isDark ? "#e0e0e0" : "#1a1a1a",
                    "& fieldset": {
                      borderColor: isDark
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: isDark
                        ? "rgba(255, 255, 255, 0.3)"
                        : "rgba(0, 0, 0, 0.3)",
                    },
                  },
                }}
              />

              <Button
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#2563EB",
                  color: "#121826",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: isDark
                      ? "rgba(37, 99, 235, 0.15)"
                      : "rgba(37, 99, 235, 0.08)",
                  },
                }}
              >
                Refresh
              </Button>

              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setIsDrawerOpen(true);
                }}
                variant="contained"
                size="small"
                sx={{
                  background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                  whiteSpace: "nowrap",
                }}
              >
                Add URD Purchase
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* AG Grid Table */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            position: "relative",
            height: "calc(100vh - 400px)",
            minHeight: "500px",
            background: isDark
              ? "linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)"
              : "linear-gradient(180deg, #ffffff 0%, rgba(248, 250, 252, 0.26) 100%)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.06)"
              : "1px solid rgba(0, 0, 0, 0.04)",
            boxShadow: isDark
              ? "0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"
              : "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.8) inset",
          }}
        >
          <CardContent sx={{ p: 0, height: "100%" }}>
            <Box
              className={isDark ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
              sx={{
                width: "100%",
                height: "100%",
                "& .ag-root-wrapper": {
                  border: "none !important",
                  borderRadius: "20px",
                },
                "& .ag-header": {
                  background: isDark
                    ? "linear-gradient(180deg, #252536 0%, #1e1e2d 100%) !important"
                    : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%) !important",
                  borderBottom: isDark
                    ? "1px solid rgba(255, 255, 255, 0.08) !important"
                    : "1px solid rgba(0, 0, 0, 0.06) !important",
                },
                "& .ag-header-cell": {
                  fontWeight: "700 !important",
                  fontSize: "12px !important",
                  textTransform: "uppercase !important",
                  letterSpacing: "0.8px !important",
                  color: isDark ? "#94a3b8 !important" : "#64748b !important",
                  borderRight: "none !important",
                  transition: "background 0.2s ease !important",
                  "&:hover": {
                    background: isDark
                      ? "rgba(37, 99, 235, 0.1) !important"
                      : "rgba(37, 99, 235, 0.05) !important",
                  },
                },
                "& .ag-body-viewport": {
                  background: isDark
                    ? "#16161a !important"
                    : "#ffffff !important",
                },
                "& .ag-row": {
                  borderBottom: isDark
                    ? "1px solid rgba(255, 255, 255, 0.04) !important"
                    : "1px solid rgba(0, 0, 0, 0.03) !important",
                  transition: "all 0.2s ease !important",
                  "&:hover": {
                    background: isDark
                      ? "linear-gradient(90deg, rgba(37, 99, 235, 0.08), rgba(139, 92, 246, 0.05)) !important"
                      : "linear-gradient(90deg, rgba(37, 99, 235, 0.04), rgba(139, 92, 246, 0.02)) !important",
                    transform: "scale(1.001)",
                  },
                },
                "& .ag-row-odd": {
                  background: isDark
                    ? "rgba(255, 255, 255, 0.01) !important"
                    : "rgba(248, 250, 252, 0.5) !important",
                },
                "& .ag-row-even": {
                  background: isDark
                    ? "transparent !important"
                    : "#ffffff !important",
                },
                "& .ag-cell": {
                  display: "flex !important",
                  alignItems: "center !important",
                  color: isDark ? "#e2e8f0 !important" : "#1e293b !important",
                  fontSize: "14px !important",
                  fontWeight: "500 !important",
                  borderRight: "none !important",
                  padding: "0 16px !important",
                },
              }}
            >
              <AgGridReact
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={urdPurchase || []}
                defaultColDef={defaultColDef}
                gridOptions={gridOptions}
                loadingOverlayComponent={loadingOverlayComponent}
                noRowsOverlayComponent={noRowsOverlayComponent}
                rowHeight={68}
                headerHeight={52}
                animateRows={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                suppressContextMenu={true}
                suppressMenuHide={false}
                suppressMovableColumns={true}
                context={{
                  handlePrint,
                }}
                onGridReady={(params) => {
                  params.api.sizeColumnsToFit();
                  setIsGridReady(true);
                  window.addEventListener("resize", () => {
                    setTimeout(() => params.api.sizeColumnsToFit());
                  });
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Side Drawer */}
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={() => !isSaving && setIsDrawerOpen(false)}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 2,
            "& .MuiDrawer-paper": {
              width: { xs: "100%", sm: 600 },
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              background: isDark
                ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
              borderLeft: `1px solid ${alpha(
                isDark ? theme.palette.divider : "#3b82f6",
                isDark ? 0.1 : 0.15
              )}`,
              boxShadow: isDark
                ? "0 0 60px rgba(0, 0, 0, 0.4)"
                : "0 0 60px rgba(59, 130, 246, 0.15)",
            },
          }}
        >
          {/* Drawer Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2.5,
              borderBottom: `1px solid ${alpha(isDark ? "#475569" : "#e2e8f0", 0.2)}`,
              background: isDark
                ? alpha("#0f172a", 0.5)
                : alpha("#f8fafc", 0.8),
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: isDark
                  ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                  : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Receipt /> Add URD Purchase
            </Typography>
            <IconButton
              onClick={() => setIsDrawerOpen(false)}
              disabled={isSaving}
              sx={{
                color: isDark ? "#94a3b8" : "#64748b",
                "&:hover": {
                  backgroundColor: alpha(isDark ? "#475569" : "#e2e8f0", 0.2),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer Content */}
          <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
            <form onSubmit={handleSubmit}>
              {/* Bill Number - Auto-generated */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.75,
                    fontWeight: 500,
                    color: isDark ? "#cbd5e1" : "#475569",
                    fontSize: "0.8rem",
                  }}
                >
                  Bill Number <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <TextField
                  name="billNo"
                  value={formData.billNo}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      backgroundColor: alpha(
                        isDark ? "#0f172a" : "#f1f5f9",
                        isDark ? 0.6 : 0.8
                      ),
                      "& .MuiOutlinedInput-input": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      },
                    },
                  }}
                />
              </Box>

              {/* Dealer Information */}
              <Card
                elevation={0}
                sx={{
                  mb: 2.5,
                  borderRadius: 2,
                  backdropFilter: "blur(10px)",
                  backgroundColor: alpha(
                    isDark ? "#1e293b" : "#ffffff",
                    isDark ? 0.4 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? "#334155" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Person sx={{ color: isDark ? "#60a5fa" : "#3b82f6", fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      }}
                    >
                      Dealer Information
                    </Typography>
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        name="name"
                        label="Dealer Name *"
                        value={formData.name}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        required
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.875rem",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="phoneNumber"
                        label="Phone Number"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.875rem",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="address"
                        label="Address"
                        value={formData.address}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.875rem",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="aadharCardNo"
                        label="Aadhar Card"
                        value={formData.aadharCardNo}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.875rem",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        name="panNo"
                        label="PAN Card"
                        value={formData.panNo}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: alpha(
                              isDark ? "#0f172a" : "#ffffff",
                              isDark ? 0.6 : 0.8
                            ),
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.875rem",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "0.8rem",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Transaction Details */}
              <Card
                elevation={0}
                sx={{
                  mb: 2.5,
                  borderRadius: 2,
                  backdropFilter: "blur(10px)",
                  backgroundColor: alpha(
                    isDark ? "#1e293b" : "#ffffff",
                    isDark ? 0.4 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? "#334155" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Event sx={{ color: isDark ? "#fbbf24" : "#f59e0b", fontSize: 20 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      }}
                    >
                      Transaction Details
                    </Typography>
                  </Box>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="Date"
                          value={formData.date ? new Date(formData.date) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const formattedDate = format(newValue, 'yyyy-MM-dd');
                              setFormData(prev => ({ ...prev, date: formattedDate }));
                            } else {
                              setFormData(prev => ({ ...prev, date: '' }));
                            }
                          }}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? "#0f172a" : "#ffffff",
                                    isDark ? 0.6 : 0.8
                                  ),
                                  "& .MuiOutlinedInput-input": {
                                    color: isDark ? "#e2e8f0" : "#1e293b",
                                    fontSize: "0.875rem",
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontSize: "0.8rem",
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="receiverName"
                          label="Receiver Name *"
                          value={formData.receiverName}
                          onChange={handleInputChange}
                          fullWidth
                          size="small"
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? "#0f172a" : "#ffffff",
                                isDark ? 0.6 : 0.8
                              ),
                              "& .MuiOutlinedInput-input": {
                                color: isDark ? "#e2e8f0" : "#1e293b",
                                fontSize: "0.875rem",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              color: isDark ? "#94a3b8" : "#64748b",
                              fontSize: "0.8rem",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </CardContent>
              </Card>

              {/* Items Section - Vertical Layout */}
              <Card
                elevation={0}
                sx={{
                  mb: 2.5,
                  borderRadius: 2,
                  backdropFilter: "blur(10px)",
                  backgroundColor: alpha(
                    isDark ? "#1e293b" : "#ffffff",
                    isDark ? 0.4 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? "#334155" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Inventory sx={{ color: isDark ? "#c084fc" : "#8b5cf6", fontSize: 20 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                        }}
                      >
                        Items
                      </Typography>
                      <Chip
                        label={formData.itemDetails.filter(item => item.itemName.trim() !== '').length}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    <Button
                      onClick={addItemRow}
                      size="small"
                      startIcon={<AddIcon />}
                      sx={{
                        borderRadius: 1.5,
                        background: isDark
                          ? "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)"
                          : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)",
                        color: isDark ? "#34d399" : "#10b981",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": {
                          background: isDark
                            ? "linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%)"
                            : "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)",
                        },
                      }}
                    >
                      Add Item
                    </Button>
                  </Box>

                  {/* Vertical Item Cards */}
                  <Stack spacing={2}>
                    {formData.itemDetails.map((item, index) => (
                      <Card
                        key={item.id}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          backgroundColor: alpha(
                            isDark ? "#0f172a" : "#f8fafc",
                            isDark ? 0.5 : 0.5
                          ),
                          border: `1px solid ${alpha(
                            isDark ? "#334155" : "#e2e8f0",
                            isDark ? 0.2 : 0.3
                          )}`,
                          position: 'relative',
                        }}
                      >
                        {/* Item Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: isDark ? "#cbd5e1" : "#475569",
                              fontSize: '0.8rem',
                            }}
                          >
                            Item #{index + 1}
                          </Typography>
                          <IconButton
                            onClick={() => removeItemRow(item.id)}
                            disabled={formData.itemDetails.length <= 1}
                            size="small"
                            sx={{
                              color: '#ef4444',
                              '&:hover': { backgroundColor: alpha('#ef4444', 0.1) },
                              '&.Mui-disabled': { opacity: 0.3 },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Item Fields Grid */}
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Item Name *"
                              value={item.itemName}
                              onChange={(e) => handleItemChange(item.id, 'itemName', e.target.value)}
                              size="small"
                              fullWidth
                              placeholder="Enter item name"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", isDark ? 0.6 : 0.9),
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '0.85rem',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Metal"
                              value={item.metal}
                              onChange={(e) => handleItemChange(item.id, 'metal', e.target.value)}
                              size="small"
                              fullWidth
                              placeholder="Enter metal type"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", isDark ? 0.6 : 0.9),
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '0.85rem',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Melting % *"
                              value={item.melting}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if ((val >= 0 && val <= 100) || e.target.value === '') {
                                  handleItemChange(item.id, 'melting', e.target.value);
                                }
                              }}
                              size="small"
                              fullWidth
                              placeholder="0-100"
                              InputProps={{
                                inputProps: { min: 0, max: 100 }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", isDark ? 0.6 : 0.9),
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '0.85rem',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Weight (gm) *"
                              value={item.weight}
                              onChange={(e) => handleItemChange(item.id, 'weight', e.target.value)}
                              size="small"
                              fullWidth
                              placeholder="0.00"
                              InputProps={{
                                inputProps: { step: 0.01 }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", isDark ? 0.6 : 0.9),
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '0.85rem',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Rate (₹/gm) *"
                              value={item.rate}
                              onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                              size="small"
                              fullWidth
                              placeholder="0.00"
                              InputProps={{
                                inputProps: { step: 0.01 }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", isDark ? 0.6 : 0.9),
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '0.85rem',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Amount (₹)"
                              value={(item.amount || 0).toFixed(2)}
                              size="small"
                              fullWidth
                              disabled
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? "#0f172a" : "#f1f5f9",
                                    isDark ? 0.4 : 0.6
                                  ),
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    color: '#b01c1c',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  fontSize: '0.75rem',
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    ))}
                  </Stack>

                  {/* Total Amount */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: alpha(
                        isDark ? "#1e293b" : "#f1f5f9",
                        isDark ? 0.5 : 0.6
                      ),
                      border: `1px solid ${alpha(
                        isDark ? "#475569" : "#e2e8f0",
                        isDark ? 0.3 : 0.4
                      )}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: isDark ? "#e2e8f0" : "#1e293b",
                        fontSize: '1rem',
                      }}
                    >
                      Total Amount:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.2rem',
                        color: '#b01c1c',
                      }}
                    >
                      ₹ {formData.totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Declaration */}
              <Card
                elevation={0}
                sx={{
                  mb: 2.5,
                  borderRadius: 2,
                  backdropFilter: "blur(10px)",
                  backgroundColor: alpha(
                    isDark ? "#1e293b" : "#ffffff",
                    isDark ? 0.4 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? "#334155" : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <Typography sx={{ fontSize: 18 }}>📝</Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      }}
                    >
                      Declaration
                    </Typography>
                  </Box>
                  <TextField
                    name="declaration"
                    value={formData.declaration}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        backgroundColor: alpha(isDark ? "#0f172a" : "#ffffff", isDark ? 0.6 : 0.8),
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          fontSize: '0.85rem',
                        },
                      },
                    }}
                  />
                </CardContent>
              </Card>

              {/* Form Actions */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  onClick={() => setIsDrawerOpen(false)}
                  disabled={isSaving}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    borderColor: isDark ? "#475569" : "#cbd5e1",
                    color: isDark ? "#cbd5e1" : "#475569",
                    '&:hover': {
                      borderColor: isDark ? "#64748b" : "#94a3b8",
                      backgroundColor: alpha(isDark ? "#475569" : "#cbd5e1", 0.1),
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSaving}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    background: isDark
                      ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                      : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                    boxShadow: isDark
                      ? "0 4px 12px rgba(59, 130, 246, 0.25)"
                      : "0 4px 12px rgba(37, 99, 235, 0.25)",
                    '&:hover': {
                      background: isDark
                        ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                        : "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                    },
                    '&.Mui-disabled': {
                      background: alpha(isDark ? "#3b82f6" : "#2563eb", 0.3),
                    },
                  }}
                >
                  {isSaving ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      Saving...
                    </Box>
                  ) : (
                    "Save Purchase"
                  )}
                </Button>
              </Stack>
            </form>
          </Box>
        </Drawer>

        {/* Overlay */}
        {isDrawerOpen && (
          <Backdrop
            sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
            open={isDrawerOpen}
          />
        )}
      </Container>

      {/* Print Preview (hidden) */}
      {printData && (
        <div style={{ display: 'none' }} dangerouslySetInnerHTML={{ __html: printData }} />
      )}
    </Box>
  );
};

export default React.memo(URDPurchase);